import { Card, CardHeader, CardContent } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Workflow, Plus, Play, Pause, Settings, ArrowRight, Zap, CheckCircle, Clock } from 'lucide-react';
import { Badge } from '../../../components/Badge';

export function Workflows() {
    return (
        <div className="space-y-6 flex flex-col h-[calc(100vh-8rem)]">
            <div className="flex justify-between items-center shrink-0">
                <div>
                    <h1 className="tracking-tight text-foreground font-display text-page-title">Workflows</h1>
                    <p className="text-muted-foreground">Visual builder for business processes and agile states.</p>
                </div>
                <Button variant="primary">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Workflow
                </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 flex-1 min-h-0">
                <div className="md:col-span-1 space-y-4 overflow-y-auto">
                    <h3 className="text-foreground mb-2 font-display text-card-title">Active Workflows</h3>
                    
                    <Card className="cursor-pointer border-primary ring-1 ring-primary/20">
                        <CardContent className="p-4 flex flex-col gap-2">
                            <div className="flex justify-between items-start">
                                <h4 className="text-foreground text-sm font-semibold">Bug Triage Process</h4>
                                <Badge variant="success">Active</Badge>
                            </div>
                            <p className="text-xs text-muted-foreground">Automatically routes incoming bug reports based on severity and tags.</p>
                            <div className="flex items-center justify-between mt-2 pt-2 border-t border-border">
                                <div className="text-xs text-muted-foreground flex items-center">
                                    <Clock className="w-3 h-3 mr-1" /> Last run: 5m ago
                                </div>
                                <div className="flex gap-1">
                                    <Button variant="ghost" size="icon" className="h-6 w-6"><Pause className="w-3 h-3" /></Button>
                                    <Button variant="ghost" size="icon" className="h-6 w-6"><Settings className="w-3 h-3" /></Button>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="cursor-pointer hover:border-primary/50 transition-colors">
                        <CardContent className="p-4 flex flex-col gap-2">
                            <div className="flex justify-between items-start">
                                <h4 className="text-foreground text-sm font-semibold">Feature Approval</h4>
                                <Badge variant="success">Active</Badge>
                            </div>
                            <p className="text-xs text-muted-foreground">Requires PM and Eng Lead sign-off before development begins.</p>
                            <div className="flex items-center justify-between mt-2 pt-2 border-t border-border">
                                <div className="text-xs text-muted-foreground flex items-center">
                                    <Clock className="w-3 h-3 mr-1" /> Last run: 2h ago
                                </div>
                                <div className="flex gap-1">
                                    <Button variant="ghost" size="icon" className="h-6 w-6"><Pause className="w-3 h-3" /></Button>
                                    <Button variant="ghost" size="icon" className="h-6 w-6"><Settings className="w-3 h-3" /></Button>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="cursor-pointer opacity-70 hover:opacity-100 transition-opacity">
                        <CardContent className="p-4 flex flex-col gap-2">
                            <div className="flex justify-between items-start">
                                <h4 className="text-foreground text-sm font-semibold">Sprint Retrospective</h4>
                                <Badge variant="secondary">Paused</Badge>
                            </div>
                            <p className="text-xs text-muted-foreground">Generates a summary report and action items after sprint close.</p>
                            <div className="flex items-center justify-between mt-2 pt-2 border-t border-border">
                                <div className="text-xs text-muted-foreground flex items-center">
                                    <Clock className="w-3 h-3 mr-1" /> Last run: 1w ago
                                </div>
                                <div className="flex gap-1">
                                    <Button variant="ghost" size="icon" className="h-6 w-6"><Play className="w-3 h-3" /></Button>
                                    <Button variant="ghost" size="icon" className="h-6 w-6"><Settings className="w-3 h-3" /></Button>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="md:col-span-2 flex flex-col min-h-0 bg-card border border-border rounded-lg overflow-hidden">
                    <div className="p-4 border-b border-border bg-secondary/10 flex justify-between items-center shrink-0">
                        <h3 className="text-foreground flex items-center gap-2 font-display text-card-title">
                            <Workflow className="w-4 h-4 text-primary" />
                            Bug Triage Process Flow
                        </h3>
                        <div className="flex gap-2">
                            <Button variant="outline" size="sm">Edit Flow</Button>
                            <Button variant="primary" size="sm">Test Run</Button>
                        </div>
                    </div>
                    <div className="flex-1 p-8 overflow-auto bg-secondary/5 flex flex-col items-center gap-6">
                        {/* Mock Workflow Nodes */}
                        <div className="w-64 bg-card border border-border rounded-lg shadow-sm p-3 relative group">
                            <div className="absolute -top-3 -right-3 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs shadow">1</div>
                            <div className="flex items-center gap-2 mb-2">
                                <Zap className="w-4 h-4 text-warning" />
                                <span className="font-medium text-sm text-foreground">Trigger</span>
                            </div>
                            <p className="text-xs text-muted-foreground">New Issue Created in "Product Repo" with label "bug"</p>
                        </div>
                        
                        <ArrowRight className="w-6 h-6 text-muted-foreground rotate-90" />
                        
                        <div className="w-64 bg-card border border-border rounded-lg shadow-sm p-3 relative group">
                            <div className="absolute -top-3 -right-3 w-6 h-6 bg-secondary-foreground text-secondary rounded-full flex items-center justify-center text-xs shadow">2</div>
                            <div className="flex items-center gap-2 mb-2">
                                <Settings className="w-4 h-4 text-primary" />
                                <span className="font-medium text-sm text-foreground">Condition</span>
                            </div>
                            <p className="text-xs text-muted-foreground">Check severity field</p>
                        </div>

                        <div className="flex gap-16 relative">
                            <div className="absolute top-0 left-1/2 w-full h-px bg-muted-foreground/30 -translate-x-1/2 -z-10"></div>
                            <div className="absolute top-0 left-[25%] w-px h-6 bg-muted-foreground/30 -z-10"></div>
                            <div className="absolute top-0 right-[25%] w-px h-6 bg-muted-foreground/30 -z-10"></div>
                            
                            <div className="mt-6 flex flex-col items-center gap-6">
                                <div className="text-xs font-medium text-destructive bg-destructive/10 px-2 rounded-full absolute -top-3 left-[20%]">High/Critical</div>
                                <div className="w-48 bg-card border border-border rounded-lg shadow-sm p-3 relative">
                                    <div className="absolute -top-3 -right-3 w-6 h-6 bg-secondary-foreground text-secondary rounded-full flex items-center justify-center text-xs shadow">3a</div>
                                    <div className="flex items-center gap-2 mb-2">
                                        <CheckCircle className="w-4 h-4 text-success" />
                                        <span className="font-medium text-sm text-foreground">Action</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground">Assign to On-Call Dev & Send Slack Alert</p>
                                </div>
                            </div>
                            
                            <div className="mt-6 flex flex-col items-center gap-6">
                                <div className="text-xs font-medium text-muted-foreground bg-secondary px-2 rounded-full absolute -top-3 right-[25%]">Low/Medium</div>
                                <div className="w-48 bg-card border border-border rounded-lg shadow-sm p-3 relative">
                                    <div className="absolute -top-3 -right-3 w-6 h-6 bg-secondary-foreground text-secondary rounded-full flex items-center justify-center text-xs shadow">3b</div>
                                    <div className="flex items-center gap-2 mb-2">
                                        <CheckCircle className="w-4 h-4 text-success" />
                                        <span className="font-medium text-sm text-foreground">Action</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground">Add to Backlog & Assign Triage Tag</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
